import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import AppPageHeader from "components/AppPageHeader";
import FilterBar from "components/Filters/FilterBar";
import { IFilterDataItem } from "components/Filters/filtersOptions";
import { getCheckboxesFilterValue } from "components/Filters/helper";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import {
  clearConditionBookFilters,
  setConditionBookFilters,
  setSelectedFacilityId,
  setSelectedRaceId
} from "store/actions/conditionBookPage";
import { setBackArrowVisibility } from "store/actions/sidebar";
import { mainTracks } from "utils/facilities";
import ConditionBook from "./ConditionBook";
import ConditionBookFilters from "./ConditionBookFilters";
import ConditionBooksSelector from "./ConditionBooksSelector";
import {
  createModel,
  getConditionBookGenderFilterData,
  getDateRange
} from "./helper";
import { IAvailableFacility, Periods } from "./interfaces";
import { useStateConditionBookFilters } from "./useStateConditionBookFilters";

import useStyles from "./styles";

const currentDate = new Date();

const ConditionBookPage = () => {
  const classes = useStyles();
  const { currentUser } = useLoggedInUser();
  const dispatch = useDispatch();
  const {
    selectedConditions,
    selectedGender,
    selectedAge,
    selectedDistances,
    selectedRestrictions,
    hasSavedFiltersValues
  } = useStateConditionBookFilters();

  const [modelIsWorking, setModelIsWorking] = useState<boolean>(false);
  const [conditionsFilterValues, setConditionsFilterValues] = useState<
    IFilterDataItem[]
  >([]);
  const [selectedPeriod, setSelectedPeriod] = useState<Periods>(
    Periods.current
  );
  const [model, setModel] = useState(null);
  const [rawRaces, setRawRaces] = useState<racehorse360.IRaceCondition[]>([]);
  const [availableFacilities, setAvailableFacilities] = useState<
    IAvailableFacility[]
  >([]);
  const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false);

  const { selectedFacilityId } = useAppSelector(
    state => state?.conditionBookPage
  );

  const currentFacilityId =
    selectedFacilityId || currentUser.facilityIdsListFromRoles[0];

  const {
    usePullRaceConditions,
    usePullRaceConditionsFacilities,
    usePullRegionPreferredConditions,
    useGetFacility
  } = useRacehorse360Api();

  const { data: facilityData, isLoading: isLoadingFacilityData } =
    useGetFacility(
      {
        id: selectedFacilityId,
        getOptions: {
          select: ["region"]
        }
      },
      {
        enabled: Boolean(selectedFacilityId)
      }
    );

  const { isLoading: isLoadingRegionPreferredConditions } =
    usePullRegionPreferredConditions(
      {
        id: {
          value: facilityData?.region?.id || null
        }
      },
      {
        enabled: Boolean(!isLoadingFacilityData && facilityData),
        onSuccess: data => {
          const conditionsDataFilter: IFilterDataItem[] =
            data?.regionPreferredConditions?.raceTypes.map(
              (race: racehorse360.RaceType) => {
                return {
                  label: race.name,
                  value: race.name
                };
              }
            ) || [];
          const existingRegionConditions = new Set(
            conditionsDataFilter.map(item => item.value?.toUpperCase())
          );
          const filteredSelectedConditions = selectedConditions?.filter(item =>
            existingRegionConditions.has(item?.toUpperCase())
          );
          const selectedConditionsValue = filteredSelectedConditions?.length
            ? filteredSelectedConditions
            : null;

          setConditionsFilterValues(conditionsDataFilter);
          dispatch(
            setConditionBookFilters({
              selectedGender,
              selectedDistances,
              selectedAge,
              selectedRestrictions,
              selectedConditions: selectedConditionsValue
            })
          );
        }
      }
    );

  const {
    isLoading: facilityListIsLoading,
    isFetching: facilityListIsFetching
  } = usePullRaceConditionsFacilities(
    "usePullRaceConditionsFacilities",
    {
      facilityIds:
        !currentUser.isTrainer && !currentUser.isAdmin
          ? [...currentUser.facilityIdsListFromRoles, currentFacilityId]
          : mainTracks
    },
    {
      onSuccess: (
        result: racehorse360.PullRaceConditionsFacilitiesResponse
      ) => {
        const availableFacilitiesArray = result.raceConditionsFacilities.map(
          it => {
            return {
              id: it.facilityId,
              name: it.facilityName,
              code: it.facilityCode,
              earliestRaceDate: it.earliestRaceDate,
              latestRaceDate: it.latestRaceDate,
              isDefault: currentFacilityId
                ? it.facilityId === currentFacilityId
                : it.facilityCode === "SA"
            };
          }
        );
        if (!selectedFacilityId) {
          dispatch(
            setSelectedFacilityId(
              availableFacilitiesArray.find(f => !!f.isDefault)?.id ||
                availableFacilitiesArray[0]?.id
            )
          );
        }
        setAvailableFacilities(availableFacilitiesArray);
      },
      onError: error => {
        console.log(error);
      }
    }
  );

  const { isLoading: cbIsLoading, isFetching: cbIsFetching } =
    usePullRaceConditions(
      "conditionBook",
      {
        query: {
          ...getDateRange(selectedPeriod, currentDate),
          facilityId: currentFacilityId,
          restrictions: getCheckboxesFilterValue(selectedRestrictions)?.map(
            i => +i
          ),
          distances: getCheckboxesFilterValue(selectedDistances)?.map(i => +i),
          ages: getCheckboxesFilterValue(selectedAge)?.map(i => +i),
          genders: getConditionBookGenderFilterData(selectedGender),
          conditions: getCheckboxesFilterValue(selectedConditions)
        }
      },
      {
        enabled: Boolean(
          currentFacilityId && !isLoadingRegionPreferredConditions
        ),
        onSuccess: (result: racehorse360.IPullRaceConditionsResponse) => {
          setModelIsWorking(true);
          const data = createModel(result);

          setModel(data);
          setRawRaces(result.raceConditions);
          setModelIsWorking(false);
        },
        onError: error => {
          console.log(error);
        }
      }
    );

  const isFacilityLoading = [
    facilityListIsLoading,
    facilityListIsFetching
  ].some(Boolean);
  const isBookDataLoading = [
    cbIsLoading,
    cbIsFetching,
    modelIsWorking,
    isLoadingRegionPreferredConditions,
    isLoadingFacilityData
  ].some(Boolean);

  const handleOpenFilters = () => {
    setIsOpenFilters(true);
  };

  const handleCloseFilters = () => {
    setIsOpenFilters(false);
  };

  const handleClearFilters = () => {
    dispatch(clearConditionBookFilters());
  };

  const handleSelectFilterOption = (
    facilityId: string,
    period: Periods
  ): void => {
    setSelectedPeriod(period);
    dispatch(setSelectedFacilityId(facilityId));
  };
  const isShowConditionsMatches = Boolean(
    selectedPeriod === Periods.current && currentUser.isTrainer
  );

  useEffect(() => {
    return () => {
      dispatch(setBackArrowVisibility(false));
      currentUser.isRacingOfficial && dispatch(setSelectedRaceId(null));
    };
  }, []);

  return (
    <AppPage className={classes.rootPage}>
      <AppPageHeader className={classes.header}>
        <div className={classes.leftHeaderSide}>
          {isFacilityLoading && <Loader size={20} className={classes.loader} />}
          {currentFacilityId && availableFacilities.length > 0 && (
            <ConditionBooksSelector
              selectedFacility={currentFacilityId}
              selectedPeriod={selectedPeriod}
              availableFacilities={availableFacilities}
              currentDate={currentDate}
              onSelectFilterOption={handleSelectFilterOption}
            />
          )}
        </div>
        <FilterBar
          onOpenFilters={handleOpenFilters}
          isOpenFilters={isOpenFilters}
          onClearFilters={handleClearFilters}
          hasSavedFilters={hasSavedFiltersValues}
        />
      </AppPageHeader>
      <AppPageContent className={classes.page}>
        {isBookDataLoading && <Loader overlay />}

        {model && rawRaces.length > 0 && !isBookDataLoading && (
          <ConditionBook
            bookData={model}
            rawRaces={rawRaces}
            isShowConditionsMatches={isShowConditionsMatches}
          />
        )}

        {availableFacilities.length > 0 &&
          !isBookDataLoading &&
          !rawRaces.length && (
            <div className={classes.noDataHolder}>
              <div>No information available for the selected</div>
              <div>condition book and period</div>
            </div>
          )}
      </AppPageContent>

      {isOpenFilters && (
        <ConditionBookFilters
          onClose={handleCloseFilters}
          conditionsDataFilter={conditionsFilterValues}
        />
      )}
    </AppPage>
  );
};

export default ConditionBookPage;
